import Layout from '@/components/feature/Layout';
import SEOHead from '@/components/base/SEOHead';
import { localBusinessSchema, buildBreadcrumbSchema, buildArticleSchema, buildGraphSchema } from '@/components/base/schemas';
import { Link } from 'react-router-dom';

const articleSchema = buildArticleSchema(
  'Double vitrage embué : que faire et peut-on le réparer ?',
  'Double vitrage embué entre les deux vitres ? Pourquoi la buée apparaît (joint HS), peut-on réparer, quelles solutions.',
  '2026-05-03'
);

const breadcrumbSchema = buildBreadcrumbSchema([
  { name: 'Accueil', item: 'https://fenetre-paris15.fr/' },
  { name: 'Blog', item: 'https://fenetre-paris15.fr/blog/' },
  { name: 'Double vitrage embué : que faire ?' },
]);

export default function ArticleVitragePage() {
  return (
    <Layout>
      <SEOHead
        title="Double Vitrage Embué — Causes et Que Faire | DF PRO"
        description={`Double vitrage embué entre les deux vitres ? Pourquoi la buée apparaît (joint HS), peut-on réparer, quelles solutions. DF PRO Paris 15.`}
        canonicalUrl="https://fenetre-paris15.fr/blog/double-vitrage-embue-que-faire/"
        schema={buildGraphSchema(localBusinessSchema, breadcrumbSchema, articleSchema)}
      />

      <div className="bg-brand-mint/30 py-3">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <nav aria-label="Fil d'Ariane" className="text-xs text-brand-text/60">
            <Link to="/" className="hover:text-brand-accent">Accueil</Link><span className="mx-2">/</span><Link to="/blog/" className="hover:text-brand-accent">Blog</Link><span className="mx-2">/</span><span className="text-brand-accent font-medium">Double vitrage embué</span>
          </nav>
        </div>
      </div>

      <article className="py-12 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="mb-8">
            <span className="text-brand-accent text-sm font-medium">3 mai 2026</span>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-brand-dark mt-2 mb-4">Double vitrage embué : que faire et peut-on le réparer ?</h1>
            <p className="text-brand-text/60 text-base">Vous avez de la condensation entre les deux vitres de votre fenêtre ? Voici pourquoi cela arrive et ce que vous pouvez faire.</p>

            {/* Image d'en-tête article */}
            <div className="aspect-[16/9] rounded-2xl overflow-hidden mt-6">
              <img
                src="/images/dfpro-article-vitrage.webp"
                alt="Double vitrage embué dans intérieur parisien — article réparation DF PRO Paris 15"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>

          <div className="prose prose-sm max-w-none text-brand-text/70 leading-relaxed space-y-4">
            <h2 className="font-heading text-xl font-bold text-brand-dark">Pourquoi mon double vitrage est-il embué ?</h2>
            <p>Un double vitrage est constitué de <strong>deux vitres séparées par une lame d'air ou de gaz argon</strong>, scellées hermétiquement par un joint périphérique. Quand vous voyez de la buée <strong>entre les deux vitres</strong>, cela signifie que le <strong>joint d'étanchéité périphérique a cédé</strong>. L'humidité de l'air ambiant s'est infiltrée dans l'espace entre les vitres, et quand la température baisse, elle se condense.</p>
            <p>Ne confondez pas avec la <strong>condensation sur la face intérieure</strong> de la vitre (côté pièce), qui est un phénomène normal d'humidité ambiante et qui se règle en aérant. La buée <strong>entre les vitres</strong> est le signe d'une défaillance du vitrage isolant.</p>

            <h2 className="font-heading text-xl font-bold text-brand-dark">Les conséquences d'un double vitrage embué</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Perte d'isolation thermique :</strong> le gaz argon s'est échappé, le vitrage n'isole plus correctement.</li>
              <li><strong>Visibilité réduite :</strong> la buée permanente ou les traces de calcaire dégradent l'esthétique.</li>
              <li><strong>Dégradation progressive :</strong> avec le temps, des moisissures peuvent apparaître entre les vitres.</li>
            </ul>

            <h2 className="font-heading text-xl font-bold text-brand-dark">Peut-on réparer un double vitrage embué ?</h2>
            <p>C'est la question que tout le monde se pose. La réponse est <strong>nuancée</strong> :</p>
            <p><strong>Non, on ne peut pas « dé-embuer » un double vitrage</strong> de façon durable. Certaines entreprises proposent de percer un micro-trou pour évacuer l'humidité, mais cette technique est controversée : elle ne restaure pas le gaz argon, le vitrage reste moins performant thermiquement, et le problème peut revenir. De plus, percer le vitrage fragilise sa structure.</p>
            <p><strong>La seule solution durable est de remplacer le vitrage isolant.</strong> Cette opération, appelée <strong>remplacement de vitrage</strong>, consiste à ne changer que le « bloc vitre », sans remplacer toute la fenêtre. Le cadre et l'ouvrant sont conservés. C'est une solution plus économique et plus écologique que le remplacement complet de la menuiserie.</p>

            <h2 className="font-heading text-xl font-bold text-brand-dark">Notre position chez DF PRO</h2>
            <p>DF PRO est <strong>spécialiste de la quincaillerie et des mécanismes</strong>. Le remplacement de vitrage ne fait pas partie de notre périmètre d'intervention — nous ne le faisons pas, et nous ne vous le vendrons pas. En revanche, nous pouvons <strong>diagnostiquer précisément</strong> si la buée est bien due à un joint de vitrage défaillant ou à un problème d'étanchéité de la menuiserie elle-même (joint périphérique de l'ouvrant).</p>
            <p>Si c'est bien le vitrage qui est en cause, nous vous orienterons vers un vitrier de confiance dans le 15ᵉ. Si c'est un joint de fenêtre à remplacer, nous pouvons intervenir.</p>

            <div className="bg-brand-mint/30 rounded-xl p-6 mt-6">
              <h2 className="font-heading text-xl font-bold text-brand-dark mb-3">Un doute sur votre double vitrage ?</h2>
              <p className="text-brand-text/70 text-sm mb-4">DF PRO peut diagnostiquer votre fenêtre dans le 15ᵉ. Diagnostic gratuit.</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/devis-fenetre-paris-15/" className="btn-primary text-sm">Devis gratuit</Link>
                <Link to="/reparation-fenetre-pvc-paris-15/" className="btn-outline text-sm">Fenêtres PVC</Link>
              </div>
            </div>

            <div className="flex flex-wrap gap-4 mt-8 pt-6 border-t border-gray-200">
              <Link to="/blog/" className="text-brand-accent hover:underline text-sm">← Retour au blog</Link>
              <Link to="/reparation-fenetre-pvc-paris-15/" className="text-brand-accent hover:underline text-sm">Réparation fenêtres PVC</Link>
              <Link to="/reparation-fenetre-aluminium-paris-15/" className="text-brand-accent hover:underline text-sm">Réparation fenêtres Aluminium</Link>
            </div>
          </div>
        </div>
      </article>
    </Layout>
  );
}